import RenderPipeline from "../renderpipeline";
import ShaderService from "../../shader/shader_service";
import Camera from "../../scene/camera";
import Mesh from "../../geometry/mesh";
import MeshData from "../../geometry/meshdata";
import ResourceService from "../../resource/resourceservice";
import Transform from "../transform";

export default class PlanePivotRenderPipeline extends RenderPipeline
{

    label:string;
    shader_name:string;
    texture_name:string;
    width:number;
    height:number;

    camera_buffer: GPUBuffer | null = null;
    object_buffer: GPUBuffer | null = null;
    bind_group: GPUBindGroup | null = null;
    texture_bind_group: GPUBindGroup | null = null;
    mesh: Mesh;

    constructor(key:string,shader_name:string,texture_name:string,width:number,height:number)
    {

        super(key+"_"+"plane"); //use for gpu labels
        this.label = key+"_"+"plane";
        this.shader_name = shader_name;
        this.texture_name = texture_name+".png";
        this.width = width;
        this.height = height;
        this.mesh = new Mesh();

    }

    async build(
        device: GPUDevice,
        format: GPUTextureFormat,
        shader_service: ShaderService,
        resource_service: ResourceService
      ): Promise<void> 
      {
        const shader = shader_service.get_render(this.shader_name);
        this.pipeline = device.createRenderPipeline({
          layout: "auto",
          label:this.label,
          vertex: {
            module: shader,
            entryPoint: "vs_main",
            buffers: [
              {
                arrayStride: 32,
                attributes: [
                  { shaderLocation: 0, offset: 0,  format: "float32x3" }, // position
                  { shaderLocation: 1, offset: 12, format: "float32x3" }, // normal
                  { shaderLocation: 2, offset: 24, format: "float32x2" }, // uv
                ],
              },
            ],
          },
          fragment: {
            module: shader,
            entryPoint: "fs_main",
            targets: [
              {
                format,
                blend: {
                  color: {
                    operation: "add",
                    srcFactor: "src-alpha",
                    dstFactor: "one-minus-src-alpha",
                  },
                  alpha: {
                    operation: "add",
                    srcFactor: "one",
                    dstFactor: "one-minus-src-alpha",
                  },
                },
              },
            ],
          },
          primitive: {
            topology: "triangle-list",
            cullMode: "none",
          },
          depthStencil: {
            format: "depth24plus",
            depthWriteEnabled: true,
            depthCompare: "less",
          },
        });
    
    
        this.camera_buffer = device.createBuffer({
          size: 64,
          usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        });
    
        this.object_buffer = device.createBuffer({
          size: 64,
          usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        });
    
        this.bind_group = device.createBindGroup({
          layout: this.pipeline.getBindGroupLayout(0),
          label:this.label+" cam_obj_bind_group ",
          entries: [
            { binding: 0, resource: { buffer: this.camera_buffer } },
            { binding: 1, resource: { buffer: this.object_buffer } },
          ],
        });
    
        const texture_resource = await resource_service.getTexture(this.texture_name);
        const texture_asset = texture_resource.value;
    
        this.texture_bind_group = device.createBindGroup({
          layout: this.pipeline.getBindGroupLayout(1),
          label:this.label+" tex_bind_group",
          entries: [
            { binding: 0, resource: texture_asset.sampler },
            { binding: 1, resource: texture_asset.view },
          ],
        });
    
        this.mesh.build(device, MeshData.make_pivot_offset_ground_plane(this.width, this.height,0.2));
    
      }

     prepare(device: GPUDevice, camera: Camera , transform:Transform): void 
     {
        if (!this.camera_buffer || !this.object_buffer) return;

        device.queue.writeBuffer(
            this.camera_buffer,
            0,
            camera.view_projection_matrix.buffer as ArrayBuffer,
            camera.view_projection_matrix.byteOffset,
            camera.view_projection_matrix.byteLength
            );

        device.queue.writeBuffer(
            this.object_buffer,
            0,
            transform.model_matrix.buffer as ArrayBuffer,
            transform.model_matrix.byteOffset,
            transform.model_matrix.byteLength
            );
     }

     draw(pass: GPURenderPassEncoder): void 
    {
        if (!this.pipeline || !this.bind_group || !this.texture_bind_group) return;

        pass.setPipeline(this.pipeline);
        pass.setBindGroup(0, this.bind_group);
        pass.setBindGroup(1, this.texture_bind_group);
        this.mesh.draw(pass);
   }
}